import {PerformanceLogger} from '../performance';
import {LOG_TYPE} from './const';
import {ENVIRONMENT_LEVEL} from '@/Constant';
import weirwood from '@/weirwood';
import {WizKnownError} from '@/sdk/common/error';
import * as store from '@/store';
import {getKnowledgePageRouteMatchInfo} from '@/utils/urlUtils';

export function isError(param: any) {
    return Object.prototype.toString.call(param).includes('Error');
}

export function isEvent(param: any) {
    return Object.prototype.toString.call(param).includes('Event');
}

enum ERROR_TYPE {
    capturedAPIError = 'capturedAPIError',
    capturedJSError = 'capturedJSError',
    unCapturedError ='unCapturedError',
}
export class CaptureError {
    key = 'CaptureError';
    logger: PerformanceLogger;
    ERROR_TYPE = ERROR_TYPE;
    nativeConsoleError: Console['error'] | null = null;
    currentUrl = '';
    constructor(logger: PerformanceLogger) {
        this.logger = logger;
        this.proxyConsoleError();
        this.bindWindowError();
    }

    bindWindowError = () => {
        const oldOnErrorHandle = window.onerror;
        window.onerror = (
            event: string | Event,
            source?: string,
            lineno?: number,
            colno?: number,
            error?: Error
        ) => {
            oldOnErrorHandle && oldOnErrorHandle(event, source, lineno, colno, error);
            if (isEvent(event)) {
                this.captureError((event as ErrorEvent).error, ERROR_TYPE.unCapturedError);
                return;
            }
            if (error && isError(error)) {
                this.captureError(error, ERROR_TYPE.unCapturedError);

            }
        };
    };

    uploadWebError(error: Error, type: ERROR_TYPE, opVersion?: string) {
        if (!isError(error)) {
            error = new Error('捕获未知错误');
        }
        this.logger.feLogger({
            id: '80001.8.39',
            data: {
                message: error.message,
                stack: error.stack,
                type,
                timestamp: +new Date(),
                opVersion,
            },
        });
    }

    captureError(error: Error, type: ERROR_TYPE) {
        const logger = this.logger;
        const logLevel = logger.logLevel || '';
        const opVersion = logger.getOpVersionInfo();
        if ([ENVIRONMENT_LEVEL.online, ENVIRONMENT_LEVEL.gray, ENVIRONMENT_LEVEL.qa].includes(logLevel)) {
            // 如果是已捕获错误 则直接上报
            // 数据组实际计算错误率 会过滤掉已捕获错误
            if ([ERROR_TYPE.capturedJSError, ERROR_TYPE.capturedAPIError].includes(type)) {
                this.uploadWebError(error, type, opVersion);
                error.name = type;
                weirwood && weirwood.error.captureException(error);
            } else {
                // 若是未捕获错误 需要判断是否是第一次上报未捕获错误
                const previous = this.currentUrl;
                const currentUrl = location.href;
                this.currentUrl = currentUrl;
                if (previous !== location.href) {
                    this.uploadWebError(error, type, opVersion);
                }
            }
        }
        if (logLevel !== ENVIRONMENT_LEVEL.online) {
            logger.buffer.add(this.key, {
                message: error.message,
                stack: error.stack,
                type,
                timestamp: +new Date(),
                opVersion,
            });
        }
        // 如果是被捕获的异常 且是qa环境 则上报weirwood 供研发分析代码使用
        if (
            [ERROR_TYPE.capturedJSError, ERROR_TYPE.capturedAPIError].includes(type)
            && logLevel === ENVIRONMENT_LEVEL.qa
        ) {
            weirwood && weirwood.error.captureException(error);
        }
    }
    proxyConsoleError() {
        const filters = [
            'React.forwardRef',
            'Cannot update a component',
            'canCreateDoc',
            'UNSAFE_componentWillMount',
            'The old API will be supported in all 16.x releases',
            'findDOMNode was passed an instance of DraggableCore',
        ];
        this.nativeConsoleError = window.console.error;
        window.console.error = (...args: any) => {
            const messages = args.join('');
            if (filters.find(item => messages.includes(item))) {
                return;
            }
            // eslint-disable-next-line complexity
            args.forEach((item: any) => {
                if (isError(item)) {
                    const deepCloneError = new Error();
                    if (item instanceof WizKnownError || item.toString().includes(ERROR_TYPE.capturedAPIError)) {
                        if (item instanceof WizKnownError) {
                            const fixAPIErrorMsg = {
                                requestURL: item?.options?.url || '',
                                code: item?.code || '',
                                response: item?.origin || '',
                                errMsg: item?.message || '',
                            };
                            // eslint-disable-next-line max-depth
                            if (
                                item?.origin?.returnCode === 403
                                && item?.origin?.result
                                && item?.origin?.result?.constructor === Object
                            ) {
                                deepCloneError.message = JSON.stringify({...fixAPIErrorMsg, ...item?.origin?.result});
                            } else {
                                deepCloneError.message = JSON.stringify(fixAPIErrorMsg);
                            }
                            deepCloneError.stack = item.stack;
                        }
                        this.captureError(
                            item instanceof WizKnownError ? deepCloneError : item,
                            ERROR_TYPE.capturedAPIError
                        );
                        return;
                    }
                    const {isDocPage} = getKnowledgePageRouteMatchInfo();
                    if (isDocPage) {
                        const docMode = store.getDocumentMode();
                        item.message = `${item.message} docMode: ${docMode}`;
                    }
                    // 被try catch住的error
                    this.captureError(item, ERROR_TYPE.capturedJSError);
                }
            });
            this.nativeConsoleError && this.nativeConsoleError(...args);
        };
    }

    getData() {
        return {
            type: this.key,
            logType: LOG_TYPE.period,
            origin: this.logger.buffer.get(this.key) || [],
        };
    }
}
